// 小程序数据分析页面
// 展示：访问概况、趋势图表、用户画像、页面排行、访问分布
import { Card, Table, Tag, Spin } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import ReactECharts from 'echarts-for-react'
import type { EChartsOption } from 'echarts'
import PageHeader from '@/components/PageHeader'
import { useMiniprogramOverview, type PageVisit } from '@/mocks/wechatApi'
import { formatNumber } from '@/lib/utils'

// 停留时长分布标签
const STAY_LABELS: Record<number, string> = {
  1: '0-3s', 2: '3-10s', 3: '10-30s', 4: '30-60s',
  5: '1-3min', 6: '3-10min', 7: '10-30min', 8: '>30min',
}

// 访问深度标签
const DEPTH_LABELS: Record<number, string> = {
  1: '1页', 2: '2页', 3: '3页', 4: '4-5页',
  5: '6-10页', 6: '11-20页', 7: '>20页',
}

// 访问来源标签
const SOURCE_LABELS: Record<number, string> = {
  1: '小程序主页', 2: '搜索', 3: '会话', 4: '扫二维码',
  7: '公众号文章', 10: '其他小程序', 11: '公众号菜单',
  13: '微信支付', 16: '收藏', 17: '卡包', 23: '广告',
  29: '任务栏', 49: '视频号',
}

// ECharts 通用配置
const CHART_BASE: EChartsOption = {
  tooltip: { trigger: 'axis' },
  grid: { left: 60, right: 20, top: 20, bottom: 30 },
}

function barOption(data: { name: string; value: number }[], color = '#07C160'): EChartsOption {
  const names = data.map((d) => d.name)
  const values = data.map((d) => d.value)
  return {
    ...CHART_BASE,
    xAxis: { type: 'category', data: names, axisLabel: { fontSize: 11 } },
    yAxis: { type: 'value', axisLabel: { formatter: (v: number) => formatNumber(v) } },
    series: [{
      type: 'bar', data: values, color,
      barMaxWidth: 32, itemStyle: { borderRadius: [4, 4, 0, 0] },
    }],
  }
}

export default function WechatApi() {
  const { data, isLoading, error } = useMiniprogramOverview()

  if (error) {
    return (
      <div>
        <PageHeader title="小程序数据" subtitle="API 数据加载失败" />
        <Card><div className="text-danger text-center py-12">加载失败: {String(error)}</div></Card>
      </div>
    )
  }

  const d = data

  // 用户画像图表
  const genderOption = barOption(d?.portrait.gender ?? [])
  const ageOption = barOption(d?.portrait.age ?? [])
  const provinceOption = barOption(
    (d?.portrait.province ?? []).slice(0, 10).map((p) => ({ name: p.name, value: p.value })),
  )
  const cityOption = barOption(
    (d?.portrait.city ?? []).slice(0, 10).map((c) => ({ name: c.name, value: c.value })),
  )

  // 访问分布图表
  const stayOption = barOption(
    (d?.distribution.stay_time ?? []).map((s) => ({
      name: STAY_LABELS[s.key] || `key${s.key}`,
      value: s.value,
    })),
  )
  const depthOption = barOption(
    (d?.distribution.depth ?? []).map((s) => ({
      name: DEPTH_LABELS[s.key] || `key${s.key}`,
      value: s.value,
    })),
    '#576B95',
  )
  const sourceOption = barOption(
    (d?.distribution.source ?? [])
      .sort((a, b) => b.value - a.value)
      .slice(0, 10)
      .map((s) => ({
        name: SOURCE_LABELS[s.key] || `来源${s.key}`,
        value: s.value,
      })),
    '#FA9D3B',
  )

  // 页面排行表格
  const pageColumns: ColumnsType<PageVisit> = [
    { title: '页面路径', dataIndex: 'page_path', key: 'path', ellipsis: true, width: 280,
      render: (v: string) => <span className="font-mono text-xs">{v}</span> },
    { title: 'PV', dataIndex: 'page_visit_pv', key: 'pv', align: 'right', width: 80,
      render: (v: number) => formatNumber(v) },
    { title: 'UV', dataIndex: 'page_visit_uv', key: 'uv', align: 'right', width: 80,
      render: (v: number) => formatNumber(v) },
    { title: '入口', dataIndex: 'entrypage_pv', key: 'entry', align: 'right', width: 70,
      render: (v: number) => <span className="text-primary-600">{formatNumber(v)}</span> },
    { title: '退出', dataIndex: 'exitpage_pv', key: 'exit', align: 'right', width: 70,
      render: (v: number) => <span className="text-danger">{formatNumber(v)}</span> },
    { title: '均停(s)', dataIndex: 'page_staytime_pv', key: 'stay', align: 'right', width: 80 },
  ]

  return (
    <div>
      <PageHeader
        title="小程序数据"
        subtitle={d ? `${d.app_name} — 数据更新时间: ${d.data_date}` : '加载中...'}
      />

      <Spin spinning={isLoading}>
        {/* KPI 卡片 */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
          <KPICard label="累计用户" value={d?.summary.visit_total ?? null} />
          <KPICard label="昨日 PV" value={d?.daily_trend.visit_pv ?? null} />
          <KPICard label="昨日 UV" value={d?.daily_trend.visit_uv ?? null} />
          <KPICard label="新用户" value={d?.daily_trend.visit_uv_new ?? null} color="text-primary-600" />
          <KPICard label="打开次数" value={d?.daily_trend.session_cnt ?? null} />
          <KPICard label="人均停留" value={d?.daily_trend.stay_time_uv ?? null} unit="秒" />
          <KPICard label="访问深度" value={d?.daily_trend.visit_depth ?? null} unit="页" />
          <KPICard label="分享" value={d?.summary.share_pv ?? null} />
        </div>

        {/* 趋势对比：日 / 周 / 月 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <TrendCard title="昨日访问" trend={d?.daily_trend} />
          <TrendCard title="上周访问" trend={d?.weekly_trend} />
          <TrendCard title="上月访问" trend={d?.monthly_trend} />
        </div>

        {/* 用户画像 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <Card title="性别分布" size="small">
            <ReactECharts option={genderOption} style={{ height: 220 }} />
          </Card>
          <Card title="年龄分布" size="small">
            <ReactECharts option={ageOption} style={{ height: 220 }} />
          </Card>
          <Card title="省份 Top 10" size="small">
            <ReactECharts option={provinceOption} style={{ height: 240 }} />
          </Card>
          <Card title="城市 Top 10" size="small">
            <ReactECharts option={cityOption} style={{ height: 240 }} />
          </Card>
        </div>

        {/* 页面排行 */}
        <Card title="页面访问排行 (昨日)" size="small" className="mb-6">
          <Table
            columns={pageColumns}
            dataSource={d?.top_pages ?? []}
            rowKey="page_path"
            size="small"
            pagination={false}
            scroll={{ x: 700 }}
          />
        </Card>

        {/* 访问分布 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <Card title="停留时长分布" size="small">
            <ReactECharts option={stayOption} style={{ height: 220 }} />
          </Card>
          <Card title="访问深度分布" size="small">
            <ReactECharts option={depthOption} style={{ height: 220 }} />
          </Card>
          <Card title="访问来源 Top 10" size="small">
            <ReactECharts option={sourceOption} style={{ height: 220 }} />
          </Card>
        </div>

        <div className="text-xs text-ink-400 text-center mt-4 mb-2">
          数据来源：微信官方 API (api.weixin.qq.com) · 小程序「{d?.app_name ?? '—'}」· AppID: {(d?.app_id ?? '—').slice(0, 8)}****
        </div>
      </Spin>
    </div>
  )
}

// ====== 子组件 ======

export function KPICard({ label, value, unit, color }: {
  label: string
  value: number | null
  unit?: string
  color?: string
}) {
  return (
    <div className="card flex flex-col items-center gap-1 py-3 px-2">
      <span className="text-xs text-ink-500">{label}</span>
      <span className={`text-xl font-semibold num ${color || 'text-ink-900'}`}>
        {value != null ? formatNumber(value) : '—'}
      </span>
      {unit && <span className="text-xs text-ink-400">{unit}</span>}
    </div>
  )
}

function TrendCard({ title, trend }: {
  title: string
  trend?: { date: string; session_cnt: number; visit_pv: number; visit_uv: number; visit_uv_new: number; stay_time_uv: number; visit_depth: number }
}) {
  if (!trend) return null
  return (
    <Card title={title} size="small">
      <div className="flex flex-col gap-3">
        <StatRow label="日期" value={trend.date} />
        <StatRow label="打开次数" value={formatNumber(trend.session_cnt)} />
        <StatRow label="PV" value={formatNumber(trend.visit_pv)} highlight />
        <StatRow label="UV" value={formatNumber(trend.visit_uv)} highlight />
        <StatRow label="新用户" value={formatNumber(trend.visit_uv_new)} color="text-primary-600" />
        <StatRow label="人均停留" value={`${trend.stay_time_uv.toFixed(1)}秒`} />
        <StatRow label="访问深度" value={`${trend.visit_depth.toFixed(2)}页`} />
      </div>
    </Card>
  )
}

function StatRow({ label, value, highlight, color }: {
  label: string; value: string; highlight?: boolean; color?: string
}) {
  return (
    <div className="flex justify-between items-center text-sm">
      <span className="text-ink-500">{label}</span>
      <span className={`num ${color || (highlight ? 'text-ink-900 font-semibold' : 'text-ink-700')}`}>
        {value}
      </span>
    </div>
  )
}
